import { useState } from 'react';
import { ApiError, api } from '../../lib/api/client.js';
import { Button } from '../../ui/primitives/Button.tsx';
import { Text } from '../../ui/primitives/Text.tsx';
import styles from './profile.module.css';

/**
 * Follow, unfollow, and say what happened when neither worked.
 *
 * OPTIMISTIC, BECAUSE THE SERVER IS IDEMPOTENT. `PUT` and `DELETE` on a follow
 * leave the same state however many times they arrive, so flipping the label
 * before the response lands risks nothing that a failure cannot put back — and
 * the alternative, a spinner on a control whose whole job is to feel like a
 * decision, makes following somebody feel like filing a request.
 *
 * THE TWO REFUSALS MEAN DIFFERENT THINGS and are reported differently.
 *
 * 409 is your own block: something you did and can undo, so it says so.
 *
 * 404 is the learner not being there — which is what somebody who has blocked
 * YOU looks like, deliberately and indistinguishably from a deleted account.
 * The message therefore says the profile is unavailable and stops. Saying
 * "this person blocked you" would hand the blocked party exactly the
 * information a block exists to withhold, and would make the block itself into
 * a message one learner can send another.
 */
export function FollowButton({
  userId,
  following,
  onChange,
}: {
  userId: string;
  following: boolean;
  onChange: (following: boolean) => void;
}) {
  const [problem, setProblem] = useState<string | null>(null);

  async function toggle() {
    const next = !following;
    setProblem(null);
    onChange(next);

    try {
      await (next ? api.follow(userId) : api.unfollow(userId));
    } catch (error) {
      onChange(!next);
      setProblem(describe(error));
    }
  }

  return (
    <div className={styles.followControl}>
      <Button variant={following ? 'secondary' : 'primary'} onClick={() => void toggle()}>
        {following ? 'Seguindo' : 'Seguir'}
      </Button>

      {problem ? (
        <Text as="p" variant="body-sm" tone="muted">
          {problem}
        </Text>
      ) : null}
    </div>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return 'Você bloqueou esta pessoa. Desbloqueie para seguir.';
    if (error.status === 404) return 'Este perfil não está disponível.';
  }
  return 'Não foi possível salvar agora. Tente de novo.';
}
